import { useCallback, useMemo } from 'react';
import { TrashIcon } from 'lucide-react';

import { Select } from '@/components/atoms/select';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DayOfWeek, DAYS_OF_WEEK } from '@/consts/date-time';
import { groupBy } from '@/lib/group';
import { createUUID } from '@/lib/uuid';

type DayPreference = ApiSchemas['CustomerDayPreference'];

interface Props {
  days: DayPreference[];
  update: (days: DayPreference[]) => void;
}

export function CustomerPreferencesDays({ days, update }: Props) {
  const grouped = useMemo(() => {
    const groupedByDay = groupBy(days, 'day');
    return DAYS_OF_WEEK.map((day) => ({
      day,
      preferences: groupedByDay.get(day) || [],
    }));
  }, [days]);

  const updateDay = useCallback(
    (day: DayOfWeek, preferences: DayPreference[]) => {
      const newDays = days.filter((d) => d.day !== day);
      update([...newDays, ...preferences]);
    },
    [days, update]
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <h3 className='text-heading-md'>Day preferences</h3>
      <div className='flex flex-col'>
        {grouped.map((day) => (
          <DayPreferenceEditor
            key={day.day}
            day={day.day}
            preferences={day.preferences}
            update={(preferences) => updateDay(day.day, preferences)}
          />
        ))}
      </div>
    </div>
  );
}

interface DayPreferenceEditorProps {
  day: DayOfWeek;
  preferences: DayPreference[];
  update: (preferences: DayPreference[]) => void;
}

function DayPreferenceEditor({ day, preferences, update }: DayPreferenceEditorProps) {
  const isActive = useMemo(() => {
    return preferences.length > 0;
  }, [preferences]);

  const onToggle = useCallback(() => {
    update(
      isActive
        ? []
        : [{ id: createUUID(), day, presence: 'ANY', preference: 'PREFERRED', start_time: '09:00', end_time: '17:00' }]
    );
  }, [isActive, day, update]);

  const onAddSlot = useCallback(() => {
    update([
      ...preferences,
      { id: createUUID(), day, presence: 'ANY', preference: 'PREFERRED', start_time: '09:00', end_time: '17:00' },
    ]);
  }, [day, preferences, update]);

  const onRemoveSlot = useCallback(
    (slotId?: string) => {
      update(preferences.filter((slot) => slot.id !== slotId));
    },
    [preferences, update]
  );

  const onUpdateSlot = useCallback(
    (slotId: string | undefined, updates: Partial<DayPreference>) => {
      update(preferences.map((slot) => (slot.id === slotId ? { ...slot, ...updates } : slot)));
    },
    [preferences, update]
  );

  return (
    <div className='flex flex-col gap-4 border-t-[0.5px] py-3'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <Switch checked={isActive} onCheckedChange={() => onToggle()} />
        <div className='text-base font-medium capitalize'>{day.toLowerCase()}</div>
      </div>
      {/* Content */}
      {isActive && (
        <div className='flex flex-col gap-4'>
          {preferences.map((slot) => (
            <div key={slot.id} className='flex items-center gap-2'>
              <Input
                type='time'
                value={slot.start_time ?? ''}
                onChange={(e) => onUpdateSlot(slot.id, { start_time: e.target.value })}
                className='w-32'
              />
              <span className='text-sm text-muted-foreground'>to</span>
              <Input
                type='time'
                value={slot.end_time ?? ''}
                onChange={(e) => onUpdateSlot(slot.id, { end_time: e.target.value })}
                className='w-32'
              />
              <Select
                options={[
                  { label: 'Any', value: 'ANY' },
                  { label: 'Customer must be home', value: 'REQUIRED' },
                  { label: 'Customer not required', value: 'NOT_REQUIRED' },
                ]}
                value={slot.presence}
                onChange={(value) => onUpdateSlot(slot.id, { presence: value })}
                className='w-52'
              />
              <Button variant='ghost' size='icon' onClick={() => onRemoveSlot(slot.id)} className='ml-auto shrink-0'>
                <TrashIcon className='h-4 w-4' />
              </Button>
            </div>
          ))}
          <Button variant='link' size='sm' onClick={onAddSlot} className='h-fit w-fit px-0'>
            Add a time slot
          </Button>
        </div>
      )}
    </div>
  );
}
